import { ReactNode, useEffect, useRef, useState } from 'react';
import { Layout, Button, Card, Space, Tag, Typography, Spin, Tooltip } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
  CheckOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { WizardFlowConfig, WizardStepDefinition } from './wizardSteps';

const { Sider, Content, Header } = Layout;
const { Title, Text } = Typography;

export interface WizardPatientSummary {
  id: string;
  fullName: string;
  age?: number;
  sexLabel?: string;
  programLabel?: string;
  statusLabel?: string;
  statusTagColor?: string;
  extraLines?: string[];
}

export interface WizardLayoutProps {
  flow: WizardFlowConfig;
  currentStep: number;
  onGoToStep: (index: number) => void | Promise<void>;
  onPrev: () => void;
  onNext: () => void | Promise<void>;
  onSaveDraft: () => void | Promise<void>;
  onSubmit?: () => void | Promise<void>;
  isSaving?: boolean;
  isSubmitting?: boolean;
  savingDraft?: boolean;
  /** Loading pe butonul principal „Salvează și continuă" (salvarea pasului curent). */
  nextLoading?: boolean;
  patientSummary: WizardPatientSummary;
  showPatientSummary?: boolean;
  children?: ReactNode;
  loading?: boolean;
  /** Optional next-button label override. Default: "Salvează și continuă". */
  nextLabel?: string;
  /** Optional submit-button label override. Default: "Trimite chestionar". */
  submitLabel?: string;
  /** D1 — indexuri (0-based) de pași dezactivați (ex: pașii per-leziune fără leziuni). */
  disabledSteps?: number[];
  /**
   * Indexuri (0-based) de pași care sunt doar markeri de fază (nu au ecran
   * propriu). Se afișează dimișat, fără număr, ca etichete de fază.
   */
  phaseOnlyStepIndexes?: readonly number[];
  /**
   * Indexul (0-based) al pasului la care butonul principal devine „finalizează".
   * Implicit: ultimul pas al fluxului (`flow.totalSteps - 1`).
   */
  lastStepIndex?: number;
  /** Motiv afișat în tooltip pe pașii dezactivați. */
  disabledReason?: string;
  /**
   * D1 — starea reală de completare per pas (0-based), verificată server-side.
   * Undefined → nu se afișează bife (NU se mai deduce completarea din poziție).
   */
  completedSteps?: boolean[];
  /** D1 — apelat la interacțiunea cu conținutul pasului (marcare modificări nesalvate). */
  onUserInteraction?: () => void;
}

/**
 * Reusable wizard layout used by DermatoWizard (15 steps) and HepatoWizard (12 steps).
 *
 * Renders:
 * - Left sidebar with the step list (clickable to jump between steps)
 * - Scroll-spy: secțiuni marcate cu `data-spy-section` + `id` în conținut apar
 *   ca sub-itemi sub pasul activ și se evidențiază la derulare
 * - Top: patient header (name, age, sex, program, status badge)
 * - Main: the active step content (children)
 * - Right: patient summary card (sticky)
 * - Footer: prev / save-draft / save-and-continue (or submit on the last step)
 */
const WizardLayout = ({
  flow,
  currentStep,
  onGoToStep,
  onPrev,
  onNext,
  onSaveDraft,
  onSubmit,
  isSaving = false,
  isSubmitting = false,
  savingDraft = false,
  nextLoading = false,
  patientSummary,
  showPatientSummary = true,
  children,
  loading = false,
  nextLabel = 'Salvează și continuă',
  submitLabel = 'Finalizează',
  disabledSteps,
  phaseOnlyStepIndexes,
  lastStepIndex,
  disabledReason = 'Pas indisponibil momentan',
  completedSteps,
  onUserInteraction,
}: WizardLayoutProps) => {
  const totalSteps = flow.totalSteps;
  const isFirstStep = currentStep <= 0;
  const isLastStep = currentStep >= (lastStepIndex ?? totalSteps - 1);
  const currentStepDef: WizardStepDefinition = flow.steps[currentStep] ?? flow.steps[0];

  const contentRef = useRef<HTMLDivElement>(null);
  const activeStepBtnRef = useRef<HTMLButtonElement>(null);
  const [spySections, setSpySections] = useState<Array<{ id: string; label: string }>>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Descoperă secțiunile din conținut (`[data-spy-section]`) și pornește scroll-spy.
  useEffect(() => {
    if (loading) {
      setSpySections([]);
      setActiveSectionId(null);
      return;
    }
    const root = contentRef.current;
    if (!root) return;

    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-spy-section]'));
    const sections = nodes.map((n, i) => ({
      id: n.id || `wizard-spy-${i}`,
      label: n.dataset.spySection || `Secțiunea ${i + 1}`,
    }));
    // Asigură id-uri stabile pentru scrollIntoView.
    nodes.forEach((n, i) => {
      if (!n.id) n.id = sections[i].id;
    });
    setSpySections(sections);
    setActiveSectionId(sections[0]?.id ?? null);

    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (top?.target instanceof HTMLElement && top.target.id) {
          setActiveSectionId(top.target.id);
        }
      },
      {
        root: null,
        // Sus: zona vizibilă sub header; jos: pragul „secțiunea curentă".
        rootMargin: '-12% 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5],
      },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [currentStep, loading, children]);

  // Ține pasul activ vizibil în meniul sticky.
  useEffect(() => {
    activeStepBtnRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentStep]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSectionId(id);
  };

  return (
    <Layout style={{ minHeight: 'calc(100vh - 200px)', background: 'transparent' }}>
      <Sider
        width={260}
        theme="light"
        style={{
          background: '#f5f7fa',
          borderRight: '1px solid #e8e8e8',
        }}
      >
        {/* Sticky: lista de pași rămâne vizibilă când conținutul din dreapta e mai lung. */}
        <div
          style={{
            position: 'sticky',
            top: 16,
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
            padding: '16px 0',
          }}
        >
        <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #e8e8e8', marginBottom: 12 }}>
          <Text strong style={{ fontSize: 12, letterSpacing: 1, color: '#8c8c8c' }}>
            HEPASKIN
          </Text>
          <div>
            <Text strong style={{ fontSize: 16 }}>
              Flux {flow.kind === 'DERMATO' ? 'Cutanat' : 'Hepatic'}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {totalSteps} pași
          </Text>
        </div>

        <div role="list" aria-label="Pași wizard">
          {flow.steps.map((step) => {
            const isActive = step.index === currentStep;
            // Bifa reflectă starea reală (server-side), NU poziția în wizard.
            const isComplete = completedSteps?.[step.index] ?? false;
            const isDisabled = disabledSteps?.includes(step.index) ?? false;
            // Marker de fază: nu are ecran propriu (ex. „Recontrol programat",
            // „Închidere episod") — se afișează dimișat, fără număr.
            const isPhaseOnly = phaseOnlyStepIndexes?.includes(step.index) ?? false;

            const button = (
              <button
                key={step.index}
                ref={isActive ? activeStepBtnRef : undefined}
                type="button"
                onClick={() => {
                  if (isDisabled) return;
                  void onGoToStep(step.index);
                }}
                disabled={isDisabled}
                aria-current={isActive ? 'step' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  background: isActive ? '#e6f4ff' : 'transparent',
                  borderLeft: isActive ? '3px solid #1890ff' : '3px solid transparent',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  opacity: isDisabled ? 0.45 : 1,
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: isActive ? '#1890ff' : isComplete ? '#52c41a' : '#fff',
                    color: isActive || isComplete ? '#fff' : '#595959',
                    border: isActive || isComplete ? 'none' : '1px solid #d9d9d9',
                    fontSize: 12,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {isDisabled ? (
                    <LockOutlined />
                  ) : isPhaseOnly ? (
                    '•'
                  ) : isComplete && !isActive ? (
                    <CheckOutlined />
                  ) : (
                    step.index + 1
                  )}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: isActive ? '#1890ff' : isPhaseOnly ? '#8c8c8c' : '#262626',
                      fontWeight: isActive ? 600 : 400,
                      fontStyle: isPhaseOnly ? 'italic' : 'normal',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step.title}
                  </div>
                  {isPhaseOnly && (
                    <div style={{ fontSize: 10, color: '#bfbfbf' }}>marker de fază</div>
                  )}
                  {step.description && !isPhaseOnly && (
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>{step.description}</div>
                  )}
                </div>
              </button>
            );

            return (
              <div key={step.index}>
                {isDisabled ? (
                  <Tooltip title={disabledReason} placement="right">
                    {button}
                  </Tooltip>
                ) : (
                  button
                )}
                {isActive && spySections.length > 0 && (
                  <div role="list" aria-label="Secțiuni pas curent" style={{ padding: '2px 0 8px 48px' }}>
                    {spySections.map((sec) => {
                      const isSecActive = sec.id === activeSectionId;
                      return (
                        <button
                          key={sec.id}
                          type="button"
                          onClick={() => scrollToSection(sec.id)}
                          aria-current={isSecActive ? 'true' : undefined}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '5px 10px',
                            borderRadius: 4,
                            fontSize: 12,
                            color: isSecActive ? '#1890ff' : '#8c8c8c',
                            fontWeight: isSecActive ? 600 : 400,
                            background: isSecActive ? '#e6f4ff' : 'transparent',
                            borderLeft: isSecActive ? '2px solid #1890ff' : '2px solid transparent',
                          }}
                        >
                          {sec.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </Sider>

      <Layout style={{ background: 'transparent' }}>
        <Header
          style={{
            background: '#fff',
            padding: '12px 24px',
            borderRadius: 8,
            marginBottom: 12,
            border: '1px solid #e8e8e8',
            height: 'auto',
            lineHeight: 'normal',
          }}
        >
          <Space size={16} wrap>
            <Text strong style={{ fontSize: 16 }}>
              HSK-{patientSummary.id.slice(0, 6).toUpperCase()}
            </Text>
            <Text strong style={{ fontSize: 16 }}>{patientSummary.fullName}</Text>
            {patientSummary.age !== undefined && <Text type="secondary">{patientSummary.age} ani</Text>}
            {patientSummary.sexLabel && <Text type="secondary">{patientSummary.sexLabel}</Text>}
            {patientSummary.programLabel && <Text type="secondary">{patientSummary.programLabel}</Text>}
            {patientSummary.statusLabel && (
              <Tag color={patientSummary.statusTagColor ?? 'blue'}>{patientSummary.statusLabel}</Tag>
            )}
          </Space>
        </Header>

        <Content
          style={{
            background: '#fff',
            padding: 24,
            borderRadius: 8,
            border: '1px solid #e8e8e8',
            minHeight: 480,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <Title level={2} style={{ margin: 0 }}>
              {currentStep + 1}. {currentStepDef.title}
            </Title>
            {currentStepDef.description && (
              <Text type="secondary">{currentStepDef.description}</Text>
            )}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: showPatientSummary ? 'minmax(0, 1fr) 320px' : 'minmax(0, 1fr)',
              gap: 24,
              alignItems: 'start',
            }}
          >
            <div
              ref={contentRef}
              style={{ minWidth: 0 }}
              onChange={onUserInteraction}
              onClickCapture={onUserInteraction}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary">Se încarcă datele…</Text>
                  </div>
                </div>
              ) : (
                children
              )}
            </div>

            {showPatientSummary && <Card
              title="Rezumativ pacient"
              size="small"
              style={{ position: 'sticky', top: 16 }}
            >
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Nume</Text>
                  <div><Text strong>{patientSummary.fullName}</Text></div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>ID</Text>
                  <div><Text>Hsk-{patientSummary.id.slice(0, 8).toUpperCase()}</Text></div>
                </div>
                {patientSummary.statusLabel && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                    <div>
                      <Tag color={patientSummary.statusTagColor ?? 'blue'}>
                        {patientSummary.statusLabel}
                      </Tag>
                    </div>
                  </div>
                )}
                {patientSummary.programLabel && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Program</Text>
                    <div><Text>{patientSummary.programLabel}</Text></div>
                  </div>
                )}
                {patientSummary.extraLines?.map((line, idx) => (
                  <div key={idx}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{line}</Text>
                  </div>
                ))}
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Status traseu</Text>
                  <div><Text strong>{currentStep + 1} / {totalSteps}</Text></div>
                </div>
              </Space>
            </Card>}
          </div>
        </Content>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
            gap: 8,
          }}
        >
          <Button
            onClick={onPrev}
            disabled={isFirstStep}
            icon={<LeftOutlined />}
          >
            Înapoi
          </Button>
          <Space>
            <Button
              onClick={() => void onSaveDraft()}
              loading={savingDraft || isSaving}
              icon={<SaveOutlined />}
            >
              Salvează draft
            </Button>
            {!isLastStep ? (
              <Button
                type="primary"
                onClick={() => void onNext()}
                loading={nextLoading}
                icon={<RightOutlined />}
                iconPosition="end"
              >
                {nextLabel}
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  if (onSubmit) void onSubmit();
                }}
                loading={isSubmitting}
                icon={<CheckOutlined />}
              >
                {submitLabel}
              </Button>
            )}
          </Space>
        </div>
      </Layout>
    </Layout>
  );
};

export default WizardLayout;
